import React from 'react';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import { setHours, setMinutes, getHours, getMinutes } from 'date-fns';

interface SharedDateTimePickerProps {
  selected: Date | null;
  onChange: (date: Date | null) => void;
  disabled?: boolean;
  datePlaceholder?: string;
  timePlaceholder?: string;
  clearTitle?: string;
  className?: string;
  showClearButton?: boolean;
  dateFormat?: string;
  timeFormat?: string;
  timeIntervals?: number;
  variant?: 'default' | 'compact';
  gap?: number;
}

interface DateTimeInputContainerProps {
  className?: string;
  variant?: 'default' | 'compact';
  gap?: number;
}

const DateTimeInputContainer = styled.div<DateTimeInputContainerProps>`
  display: grid;
  grid-template-columns: ${
    (props) =>
      props.variant === 'compact'
        ? 'auto auto auto' // All columns auto-width for compact
        : '1fr auto auto' // Default: date takes remaining space
  };
  gap: ${(props) => props.gap || 12}px;
  align-items: center;

  .date-input {
    position: relative;
    display: flex;
    align-items: center;

    /* Default (non-compact) wrapper width. Compact mode overrides this
       below — see the variant === 'compact' block. The compact override
       MUST come after this default declaration to win the CSS cascade
       (same selector specificity, last-wins). The previous version had
       the order reversed and the 140px default silently shadowed the
       160px compact value, leaving the placeholder truncated to
       "Select publish dc" — Lisa Farino, Row 18 #6 — even after the
       fix shipped. */
    .react-datepicker-wrapper {
      width: 140px;
    }

    .react-datepicker-input {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #ddd;
      border-radius: 3px;
      font-size: 14px;
      font-family: 'Gotham A', 'Gotham B', 'Helvetica Neue', Helvetica, Arial, sans-serif;
      line-height: 20px;
      color: #333;
      background: white;

      &:focus {
        outline: none;
        border-color: #00a6c2;
        box-shadow: 0 0 0 2px rgba(0, 166, 194, 0.1);
      }

      &::placeholder {
        color: #999;
      }

      &:disabled {
        background-color: #f5f5f5;
        color: #999;
        cursor: not-allowed;
      }
    }
  }

  .time-input {
    .react-datepicker-wrapper {
      width: 100px;
    }

    .react-datepicker-input {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #ddd;
      border-radius: 3px;
      font-size: 14px;
      font-family: 'Gotham A', 'Gotham B', 'Helvetica Neue', Helvetica, Arial, sans-serif;
      line-height: 20px;
      color: #333;
      background: white;

      &:focus {
        outline: none;
        border-color: #00a6c2;
        box-shadow: 0 0 0 2px rgba(0, 166, 194, 0.1);
      }

      &::placeholder {
        color: #999;
      }

      &:disabled {
        background-color: #f5f5f5;
        color: #999;
        cursor: not-allowed;
      }
    }
  }

  /* Compact-mode overrides. Must come AFTER the .date-input and .time-input
     default rules above so this block wins the CSS cascade (same selector
     specificity, last declaration wins). The compact date wrapper is sized
     to fit "Select publish date" (19 chars at 14px ≈ 142px text + 24px
     padding + clear-icon room ≈ 180px). Lisa Farino, Row 18 #6. */
  ${(props) =>
    props.variant === 'compact' &&
    `
    .date-input .react-datepicker-wrapper {
      width: 180px;
    }
    .time-input .react-datepicker-wrapper {
      width: 100px;
    }
  `}

  .clear-date {
    color: #999;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
    transition: all 0.2s ease;
    min-width: 32px;
    min-height: 32px;

    &:hover {
      color: #dc3545;
      background-color: #f8f9fa;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    svg {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    }
  }

  /* React-DatePicker specific styling */
  .react-datepicker {
    font-family: 'Gotham A', 'Gotham B', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    border: 1px solid #ddd;
    border-radius: 3px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .react-datepicker__header {
    background: #f8f9fa;
    border-bottom: 1px solid #e6e6e6;
    border-radius: 3px 3px 0 0;
    padding: 8px 0;
  }

  .react-datepicker__current-month {
    font-size: 14px;
    font-weight: 500;
    color: #333;
    margin-bottom: 8px;
  }

  .react-datepicker__navigation {
    top: 12px;

    &--previous {
      left: 12px;
    }

    &--next {
      right: 12px;
    }
  }

  .react-datepicker__day {
    width: 28px;
    height: 28px;
    line-height: 28px;
    margin: 0;
    border-radius: 3px;
    font-size: 13px;

    &:hover {
      background: #e3f2fd;
    }

    &--selected,
    &--keyboard-selected {
      background: #00a6c2;
      color: white;
    }

    &--today {
      font-weight: 500;
      color: #00a6c2;
    }
  }

  .react-datepicker__time-container {
    .react-datepicker__time {
      .react-datepicker__time-box {
        .react-datepicker__time-list {
          li.react-datepicker__time-list-item {
            padding: 8px 12px;
            font-size: 13px;

            &:hover {
              background: #e3f2fd;
            }

            &--selected {
              background: #00a6c2;
              color: white;
            }
          }
        }
      }
    }
  }
`;

export const SharedDateTimePicker: React.FC<SharedDateTimePickerProps> = ({
  selected,
  onChange,
  disabled = false,
  datePlaceholder = 'Select date',
  timePlaceholder = 'Select time',
  clearTitle = 'Clear date',
  className,
  showClearButton = true,
  dateFormat = 'MMM d, yyyy',
  timeFormat = 'h:mm aa',
  timeIntervals = 30,
  variant = 'default', // New prop
  gap, // New prop
}) => {
  const handleDateChange = (picked: Date | null) => {
    if (!picked) {
      onChange(null);
      return;
    }

    // Preserve existing time if available, or set to noon
    const existingTime = selected || new Date();
    const merged = setHours(setMinutes(picked, getMinutes(existingTime)), getHours(existingTime));
    onChange(merged);
  };

  const handleTimeChange = (picked: Date | null) => {
    if (!picked) return;

    const base = selected || new Date();
    // Copy picked hours/minutes onto the base date
    const merged = setHours(setMinutes(base, getMinutes(picked)), getHours(picked));
    onChange(merged);
  };

  const handleClear = () => {
    onChange(null);
  };

  return (
    <DateTimeInputContainer className={className} variant={variant} gap={gap}>
      <div className="date-input">
        <DatePicker
          selected={selected}
          onChange={handleDateChange}
          dateFormat={dateFormat}
          placeholderText={datePlaceholder}
          disabled={disabled}
          className="react-datepicker-input"
        />
      </div>

      <div className="time-input">
        <DatePicker
          selected={selected}
          onChange={handleTimeChange}
          showTimeSelect
          showTimeSelectOnly
          timeIntervals={timeIntervals}
          timeCaption="Time"
          dateFormat={timeFormat}
          placeholderText={timePlaceholder}
          disabled={disabled}
          className="react-datepicker-input"
        />
      </div>

      {showClearButton && selected && (
        <div
          className="clear-date"
          onClick={disabled ? undefined : handleClear}
          title={clearTitle}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M12.854 3.146a.5.5 0 0 1 0 .708l-9 9a.5.5 0 0 1-.708-.708l9-9a.5.5 0 0 1 .708 0z" />
            <path d="M3.146 3.146a.5.5 0 0 1 .708 0l9 9a.5.5 0 0 1-.708.708l-9-9a.5.5 0 0 1 0-.708z" />
          </svg>
        </div>
      )}

      {showClearButton && !selected && <div style={{ width: '32px', height: '32px' }} />}
    </DateTimeInputContainer>
  );
};
